import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ShoppingBag, 
  Store, 
  Car, 
  Shield, 
  ChevronUp,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
import { ADMIN_EMAILS } from '../../constants/brand';

interface RoleConfig {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  color: string;
  description: string;
}

const allRoles: RoleConfig[] = [
  { 
    id: 'shopper', 
    label: 'Shopper', 
    icon: ShoppingBag, 
    path: '/shop',
    color: 'bg-purple-500',
    description: 'Browse & shop'
  },
  { 
    id: 'business', 
    label: 'Business', 
    icon: Store, 
    path: '/business',
    color: 'bg-blue-500',
    description: 'Manage orders'
  },
  { 
    id: 'driver', 
    label: 'Driver', 
    icon: Car, 
    path: '/driver',
    color: 'bg-green-500',
    description: 'Deliver orders'
  },
  { 
    id: 'admin', 
    label: 'Admin', 
    icon: Shield, 
    path: '/admin',
    color: 'bg-gray-700',
    description: 'Control center'
  },
];

function isAdminEmail(email: string | null | undefined): boolean {
  return !!email && ADMIN_EMAILS.includes(email.toLowerCase());
}

export function RoleSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  if (location.pathname === '/coming-soon' || location.pathname === '/') {
    return null;
  }

  if (!user || !isAdminEmail(user.email)) {
    return null;
  }

  // Determine current role based on path
  const getCurrentRole = () => {
    if (location.pathname.startsWith('/business')) return 'business';
    if (location.pathname.startsWith('/driver')) return 'driver';
    if (location.pathname.startsWith('/admin')) return 'admin';
    return 'shopper';
  };

  const currentRole = getCurrentRole();
  const currentRoleData = allRoles.find(r => r.id === currentRole) || allRoles[0];

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Expanded menu */}
      {isExpanded && (
        <div className="absolute bottom-full right-0 mb-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden w-56">
          <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500">
            <div className="flex items-center space-x-2 text-white">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">Switch View</span>
            </div>
            <p className="text-xs text-white/80 mt-1">View as any role</p>
          </div>
          <div className="p-2">
            {allRoles.map((role) => {
              const Icon = role.icon;
              const isActive = currentRole === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => {
                    navigate(role.path);
                    setIsExpanded(false);
                  }}
                  className={cn(
                    "w-full flex items-center space-x-3 p-3 rounded-xl transition-all",
                    isActive 
                      ? "bg-gray-100" 
                      : "hover:bg-gray-50"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    role.color
                  )}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className={cn(
                      "font-medium",
                      isActive ? "text-primary" : "text-heading"
                    )}>
                      {role.label}
                    </p>
                    <p className="text-xs text-body">{role.description}</p>
                  </div>
                  {isActive && (
                    <div className="w-2 h-2 bg-primary rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "flex items-center space-x-2 px-4 py-3 rounded-full shadow-lg transition-all",
          "bg-white border border-gray-200 hover:shadow-xl",
          isExpanded && "ring-2 ring-primary ring-offset-2"
        )}
      >
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center",
          currentRoleData.color
        )}>
          <currentRoleData.icon className="h-4 w-4 text-white" />
        </div>
        <span className="font-medium text-heading">{currentRoleData.label}</span>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        )}
      </button>
    </div>
  );
}
